import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { IconCheck } from '@shared/ui/icons';
import { LegalLinks } from '@shared/ui/Footer';

/**
 * Public, no-login creator card: /c/:handle.
 * Backed by get_public_media_kit (anon). Since 29.10 it covers EVERY monitored creator, not
 * only registered ones — a brand sees real stats and recent brand work without signing up,
 * and the landing's "most booked creators" link here.
 */
interface Kit {
  handle: string; claimed: boolean; full_name: string | null; avatar: string | null; verified: boolean | null;
  city: string | null; country: string[] | null; category: string | null; bio: string | null;
  languages: string[] | null; accepts_barter: boolean | null; niches: string[]; niches_en?: string[] | null;
  prices?: Partial<Record<'reel' | 'story' | 'post', number | string>> | null;
  followers: number | null; engagement_rate: number | null; avg_likes: number | null;
  avg_comments: number | null; median_reel_views: number | null; posts: string[];
  last_post_at: string | null; ad_posts_90d: number; brands_90d_total: number;
  brands_90d: { name: string; logo: string | null; posts: number }[];
}

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(Math.round(n));
// Per-post averages read as exact numbers below 10K — 1 781 likes, not «2K».
const fmtExact = (n: number | null | undefined, loc: string) =>
  n == null ? '—' : n < 10_000 ? Math.round(n).toLocaleString(loc) : fmtK(n);

// Design tokens (DESIGN.md) — the card used its own palette and gradient numbers.
const C = {
  bg: 'var(--bg)', card: 'var(--bg-elev)', line: 'var(--border)', border: 'var(--border-strong)', tile: 'var(--surface-2)',
  text: 'var(--text)', muted: 'var(--muted)', link: 'var(--accent)',
};
const gradText = { color: 'var(--text)' } as const;

export default function MediaKit() {
  const { t, i18n } = useTranslation('public');
  const { handle } = useParams();
  const clean = (handle ?? '').replace(/^@+/, '');
  const { data, isLoading } = useSupabaseQuery<Kit | null>(
    ['public-media-kit', clean],
    async () => supabase.rpc('get_public_media_kit', { p_handle: clean }),
    { enabled: !!clean },
  );

  const wrap = {
    minHeight: '100vh', background: C.bg, color: C.text,
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center', padding: '20px 16px 40px',
  };
  const card = { width: '100%', maxWidth: 520, background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, overflow: 'hidden' };

  const header = (
    <div style={{ width: '100%', maxWidth: 520, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <Link to="/" style={{ fontWeight: 800, fontSize: 20, letterSpacing: 0.2, color: C.text, textDecoration: 'none' }}>
        Ada<span style={{ color: 'var(--accent)' }}>Mar</span>
      </Link>
      <LanguageSwitcher />
    </div>
  );

  if (isLoading) return <div style={{ ...wrap, justifyContent: 'center' }}><div style={{ color: C.muted }}>{t('kit.loading')}</div></div>;

  if (!data) return (
    <div style={wrap}>
      {header}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 10, maxWidth: 420 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{t('kit.notFoundTitle')}</div>
        <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.5 }}>{t('kit.notFoundBody', { handle: clean })}</div>
        <Link to="/" style={{ color: C.link, marginTop: 8 }}>{t('kit.toHome')}</Link>
      </div>
    </div>
  );

  const k = data;
  const lang = i18n.language === 'en' ? 'en-US' : 'ru-RU';
  const lastPost = k.last_post_at ? new Date(k.last_post_at).toLocaleDateString(lang, { day: 'numeric', month: 'long' }) : null;
  const stats: { label: string; value: string }[] = [
    { label: t('kit.followers'), value: fmtK(k.followers) },
    { label: t('kit.er'), value: k.engagement_rate != null ? `${k.engagement_rate.toLocaleString(lang, { maximumFractionDigits: 1 })}%` : '—' },
    { label: t('kit.reelViews'), value: fmtK(k.median_reel_views) },
    { label: t('kit.avgLikes'), value: fmtExact(k.avg_likes, lang) },
    { label: t('kit.avgComments'), value: fmtExact(k.avg_comments, lang) },
    { label: t('kit.adPosts90'), value: String(k.ad_posts_90d ?? 0) },
  ];
  const moreBrands = Math.max(0, (k.brands_90d_total ?? 0) - (k.brands_90d?.length ?? 0));
  const niches = (i18n.language === 'en' && k.niches_en?.length ? k.niches_en : k.niches) ?? [];
  // One plain sentence from the data — the card was numbers only, which reads as a
  // template page at the scale of ~1 600 cards.
  const cityName = k.city ? t(`kit.city.${k.city}`, { defaultValue: k.city }) : null;
  const summary = [
    t('kit.summary.who', { name: k.full_name || `@${k.handle}`, place: cityName ? t('kit.summary.place', { city: cityName }) : '' }),
    niches.length ? t('kit.summary.topics', { list: niches.join(', ').toLowerCase() }) : null,
    k.followers ? t('kit.summary.reach', { followers: fmtK(k.followers), count: k.brands_90d_total ?? 0 }) : null,
  ].filter(Boolean).join(' ');
  const priceRows = (['reel', 'story', 'post'] as const)
    .filter((f) => k.prices?.[f] != null)
    .map((f) => `${t(`kit.fmt.${f}`)} ${Math.round(Number(k.prices![f])).toLocaleString(lang)} ${t('kit.som')}`);

  return (
    <div style={wrap}>
      {header}

      <div style={card}>
        {/* Header */}
        <div style={{ padding: 20, display: 'flex', gap: 14, alignItems: 'center', borderBottom: `1px solid ${C.line}` }}>
          {k.avatar
            ? <img src={k.avatar} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #2a3548', flex: 'none' }} />
            : <div style={{ width: 72, height: 72, borderRadius: '50%', background: C.line, flex: 'none' }} />}
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.full_name || `@${k.handle}`}</span>
              {k.verified && <span title={t('kit.verified')} style={{ color: 'var(--accent)', display: 'inline-flex' }}><IconCheck size={16} /></span>}
            </h1>
            <a href={`https://instagram.com/${k.handle}`} target="_blank" rel="noreferrer" style={{ color: C.link, fontSize: 14, textDecoration: 'none' }}>@{k.handle}</a>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 3 }}>
              {[k.city ? t(`kit.city.${k.city}`, { defaultValue: k.city }) : null, k.category].filter(Boolean).join(' · ') || (k.country ?? []).join(', ')}
              {lastPost && <span>{(k.city || k.category || k.country?.length) ? ' · ' : ''}{t('kit.lastPost', { date: lastPost })}</span>}
            </div>
          </div>
        </div>

        <p style={{ margin: 0, padding: '12px 20px 0', color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>{summary}</p>

        {/* Niches */}
        {niches.length > 0 && (
          <div style={{ padding: '12px 20px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {niches.map((n) => (
              <span key={n} style={{ fontSize: 12, color: 'var(--accent-text)', background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 999, padding: '3px 10px' }}>{n}</span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {stats.map((s) => (
            <div key={s.label} style={{ background: C.tile, border: `1px solid ${C.line}`, borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 19, fontWeight: 800, ...gradText }}>{s.value}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Price — what a brand arriving from a shared link asks first. */}
        <div style={{ margin: '0 16px 14px', padding: '12px 14px', background: C.tile, border: `1px solid ${C.line}`, borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: C.muted }}>{t('kit.price')}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: priceRows.length ? C.text : C.muted }}>
            {priceRows.length ? priceRows.join(' · ') : t('kit.priceOnRequest')}
          </span>
        </div>

        {/* Brand work, last 90 days */}
        <div style={{ padding: '4px 20px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{t('kit.brandsTitle')}</div>
          {k.brands_90d?.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {k.brands_90d.map((b) => (
                <span key={b.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: C.tile, border: `1px solid ${C.line}`, borderRadius: 999, padding: '4px 10px 4px 4px' }}>
                  {b.logo
                    ? <img src={b.logo} alt="" loading="lazy" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                    : <span style={{ width: 22, height: 22, borderRadius: '50%', background: C.line }} />}
                  {b.name}
                  {b.posts > 1 && <span style={{ color: C.muted }}>× {b.posts}</span>}
                </span>
              ))}
              {moreBrands > 0 && <span style={{ fontSize: 12.5, color: C.muted, alignSelf: 'center' }}>{t('kit.moreBrands', { count: moreBrands })}</span>}
            </div>
          ) : (
            <div style={{ color: C.muted, fontSize: 13 }}>{t('kit.noBrands')}</div>
          )}
        </div>

        {/* Barter / languages */}
        {(k.accepts_barter || (k.languages?.length ?? 0) > 0) && (
          <div style={{ padding: '0 20px 8px', color: C.muted, fontSize: 12.5, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {(k.languages?.length ?? 0) > 0 && <span>{t('kit.languages', { list: k.languages!.join(', ').toUpperCase() })}</span>}
            {k.accepts_barter && <span style={{ color: '#7fd18f', display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconCheck size={13} /> {t('kit.barter')}</span>}
          </div>
        )}

        {/* Bio */}
        {k.bio && <div style={{ padding: '4px 20px 14px', color: 'var(--text-2)', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{k.bio}</div>}

        {/* Posts */}
        {k.posts?.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2, padding: 2 }}>
            {k.posts.slice(0, 8).map((src, i) => (
              <div key={i} style={{ aspectRatio: '1 / 1', background: C.tile, overflow: 'hidden' }}>
                <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Two doors: the brand that found this card, and the creator it belongs to */}
      <div style={{ width: '100%', maxWidth: 520, marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Direct works for every creator; it's the one action a brand came for. */}
        <a href={`https://ig.me/m/${k.handle}`} target="_blank" rel="noopener noreferrer"
          style={{ display: 'block', textAlign: 'center', padding: '13px 16px', minHeight: 48, boxSizing: 'border-box', borderRadius: 12, background: '#2563eb', color: '#fff', fontWeight: 600, textDecoration: 'none' }}>
          {t('kit.direct')}
        </a>
        <Link to="/login?mode=signup" style={{ display: 'block', textAlign: 'center', padding: '12px 16px', minHeight: 46, boxSizing: 'border-box', borderRadius: 12, border: `1px solid ${C.border}`, color: C.text, fontWeight: 600, textDecoration: 'none' }}>
          {t('kit.ctaBrand')}
        </Link>
        {!k.claimed && (
          <div style={{ textAlign: 'center', color: C.muted, fontSize: 13, lineHeight: 1.5 }}>
            {t('kit.claimPrompt', { handle: k.handle })}{' '}
            <Link to="/login?mode=signup&type=provider" style={{ color: C.link }}>{t('kit.claimCta')}</Link>
            {/* The creator never signed up: say where the data comes from and how to opt out. */}
            <div style={{ marginTop: 6 }}>
              {t('kit.sourceNote')}{' '}
              <Link to="/data-deletion" style={{ color: C.link }}>{t('kit.removeCta')}</Link>
            </div>
          </div>
        )}
      </div>

      <Link to="/" style={{ marginTop: 18, color: C.muted, fontSize: 12.5, textDecoration: 'none' }}>
        {t('kit.footer')} <span style={{ color: C.link }}>AdaMar</span>
      </Link>
      <LegalLinks style={{ marginTop: 6 }} />
    </div>
  );
}
